import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	Output,
	OnInit,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PersonClient } from '@models/clients';
import { IDVModalSteps, IDVStatus } from '@models/idv';
import { PersonMatterClient } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { IdvService } from '@services/idv/idv.service';
import { MatterClients } from '@state/global/matter_clients';
import { Observable } from 'rxjs';
import { IdvRequestModalComponent } from '../../idv-modal.component';
import { canCancelIDVRequest, getIdvStatus } from '@core/util/idv.utils';
import { TestHooks } from './clients-select.hooks';

@UntilDestroy()
@Component({
	selector: 'app-clients-select',
	templateUrl: './clients-select.component.html',
	styleUrls: ['./clients-select.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ClientsSelectComponent implements OnInit {
	testHooks = TestHooks;
	@Input() public hasCheckboxes = true; // figure naming out
	@Input() public selectedClients: PersonMatterClient[] = [];

	@Output() public sendModalEvent = new EventEmitter<IDVModalSteps>();
	@Output() public selectedClientsChange = new EventEmitter<PersonClient[]>();

	@Select(MatterClients.get_clientsForIDV) clients$: Observable<
		PersonMatterClient[]
	>;

	public allClientsSelected = false; // figure naming out
	public idvModalSteps = IDVModalSteps;
	public clients: PersonMatterClient[] = [];

	public showSelectedCount: boolean;
	public showEmptyState: boolean;
	public showCancel: boolean;
	public disableCancel: boolean;
	public disableSend: boolean;

	constructor(
		public dialogRef: MatDialogRef<IdvRequestModalComponent>,
		private idvService: IdvService,
	) {}

	ngOnInit() {
		this.clients$.pipe(untilDestroyed(this)).subscribe((clients) => {
			this.clients = clients.map((client) => {
				if (client?.idv) {
					return {
						...client,
						idv: {
							...client.idv,
							status: getIdvStatus(client.idv),
						},
					};
				}
				return client;
			});

			if (!this.hasCheckboxes) {
				this.clients = this.selectedClients;
			}
			this.setTemplateState(this.clients);
		});
	}

	setModalStep(step: IDVModalSteps): void {
		this.sendModalEvent.emit(step);
		this.selectedClientsChange.emit(this.selectedClients);
	}

	handleCheckboxClick(client?: PersonMatterClient): void {
		if (this.selectedClients.includes(client)) {
			this.selectedClients = this.selectedClients.filter((c) => c !== client);
		} else {
			this.selectedClients.push(client);
		}
		this.updateDisabledState();
		this.allClientsSelected = this.allSelected();
	}

	selectAll(selectAll: boolean): void {
		selectAll ? this.addAllClients() : this.removeAllClients();
		this.updateDisabledState();
		this.allClientsSelected = this.allSelected();
	}

	shouldDisableIdvCancel(): boolean {
		// if no selected clients and there are selected clients with status of completed, requires review or, expired then disable
		return this.selectedClients.length === 0 || this.hasCompletedRequests();
	}

	shouldDisableIdvRequest(): boolean {
		return !(this.selectedClients.length > 0);
	}

	updateDisabledState(): void {
		this.disableSend = this.shouldDisableIdvRequest();
		this.disableCancel = this.shouldDisableIdvCancel();
	}

	setTemplateState(clients: PersonMatterClient[]): void {
		console.log('clients', clients);
		this.showSelectedCount = clients.length > 0 && this.hasCheckboxes;
		this.showEmptyState = clients.length === 0;
		this.showCancel = canCancelIDVRequest(clients);
		this.updateDisabledState();
	}

	hasCompletedRequests(): boolean {
		return (
			this.selectedClients.filter(
				(client) =>
					client?.idv?.status === IDVStatus.FLAGGED ||
					client?.idv?.status === IDVStatus.VERIFIED ||
					client?.idv?.status === IDVStatus.REQUIRES_REVIEW ||
					client?.idv?.status === IDVStatus.REPORT_EXPIRED,
			).length > 0
		);
	}

	closeModal(): void {
		this.dialogRef.close();
	}

	getCancelButtonText(): string {
		return this.selectedClients.length > 1
			? 'Cancel IDV Requests'
			: 'Cancel IDV Request';
	}

	getSendButtonText(): string {
		return this.selectedClients.length > 1
			? 'Send IDV Requests'
			: 'Send IDV Request';
	}

	private removeAllClients() {
		this.selectedClients = [];
	}

	private addAllClients() {
		this.selectedClients = this.clients;
	}

	private allSelected(): boolean {
		return this.selectedClients.length === this.clients.length;
	}
}
